import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {theme} from '../constants';

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{outcome: 'accepted' | 'dismissed'; platform: string}>;
};

let cachedDeferredPrompt: BeforeInstallPromptEvent | null = null;

const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  !!(window.navigator as any).standalone;

const isIos = () =>
  /iphone|ipad|ipod/i.test(navigator.userAgent) &&
  !(window.navigator as any).standalone;

const isAndroidInstallable = () =>
  !isIos() && !isStandalone();

export const InstallPrompt: React.FC = () => {
  const {t} = useTranslation();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(cachedDeferredPrompt);
  const [showAndroid, setShowAndroid] = useState(false);
  const [showIos, setShowIos] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;

    const onAppInstalled = () => {
      cachedDeferredPrompt = null;
      setDeferredPrompt(null);
      setShowAndroid(false);
      setShowIos(false);
    };

    const onBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      const promptEvent = e as BeforeInstallPromptEvent;
      cachedDeferredPrompt = promptEvent;
      setDeferredPrompt(promptEvent);
    };

    window.addEventListener('appinstalled', onAppInstalled);
    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt as EventListener);

    return () => {
      window.removeEventListener('appinstalled', onAppInstalled);
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt as EventListener);
    };
  }, []);

  useEffect(() => {
    if (isStandalone()) return;

    setShowAndroid(false);
    setShowIos(false);

    const timer = setTimeout(() => {
      if (isIos()) {
        setShowIos(true);
        return;
      }

      if (isAndroidInstallable() && deferredPrompt) {
        setShowAndroid(true);
      }
    }, 3000);

    return () => clearTimeout(timer);
  }, [deferredPrompt]);

  const dismiss = () => {
    setShowAndroid(false);
    setShowIos(false);
  };

  const install = async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;

    if (choice.outcome === 'accepted') {
      cachedDeferredPrompt = null;
      setDeferredPrompt(null);
    }

    dismiss();
  };

  if (!showAndroid && !showIos) return null;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 90,
        left: '50%',
        transform: 'translateX(-50%)',
        width: 'calc(100% - 32px)',
        maxWidth: 618,
        backgroundColor: theme.colors.mainDark,
        borderRadius: 14,
        padding: '14px 16px',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
      }}
    >
      <p style={{color: theme.colors.white, fontSize: 14, margin: 0, lineHeight: 1.5}}>
        {showIos ? t('install.iosMessage') : t('install.promptMessage')}
      </p>
      <div style={{display: 'flex', gap: 8, justifyContent: 'flex-end'}}>
        <button
          type='button'
          onClick={dismiss}
          style={{
            background: 'transparent',
            border: `1px solid rgba(255,255,255,0.3)`,
            color: theme.colors.white,
            borderRadius: 8,
            padding: '6px 14px',
            fontSize: 13,
            cursor: 'pointer',
          }}
        >
          {t('install.promptDismiss')}
        </button>
        {showAndroid && deferredPrompt && (
          <button
            type='button'
            onClick={install}
            style={{
              background: theme.colors.mainColor,
              border: 'none',
              color: theme.colors.white,
              borderRadius: 8,
              padding: '6px 14px',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {t('install.promptButton')}
          </button>
        )}
      </div>
    </div>
  );
};
